import { supabase } from "@/integrations/supabase/client";

type ReadingProgressPayload = {
  user_id: string;
  resource_id: string;
  current_page: number;
  total_pages: number | null;
  scroll_position: number;
  zoom_level: number;
  last_read_at: string;
};

type ReadingSessionPayload = {
  id: string;
  user_id: string;
  resource_id: string;
  started_at: string;
  ended_at: string;
  pages_viewed: number[];
  total_seconds: number;
};

type StudySessionPayload = {
  id: string;
  user_id: string;
  resource_id: string;
  library_id: string | null;
  session_type: "reading" | "video";
  started_at: string;
  ended_at: string;
  duration_seconds: number;
};

type OfflineReaderSyncJob =
  | {
    type: "reading-progress";
    key: string;
    payload: ReadingProgressPayload;
  }
  | {
    type: "reading-session";
    key: string;
    payload: ReadingSessionPayload;
  }
  | {
    type: "study-session";
    key: string;
    payload: StudySessionPayload;
  };

const STORAGE_KEY = "athr.reader-sync-queue";
const STORAGE_UPDATED_EVENT = "athr-reader-sync-updated";
const isUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const readQueue = (): OfflineReaderSyncJob[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as OfflineReaderSyncJob[]) : [];
  } catch {
    return [];
  }
};

export const getOfflineReaderSyncQueueCount = () => readQueue().length;

const writeQueue = (queue: OfflineReaderSyncJob[]) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    window.dispatchEvent(new CustomEvent(STORAGE_UPDATED_EVENT));
  } catch {
    // Ignore storage failures and leave direct sync path intact.
  }
};

const mergeQueue = (queue: OfflineReaderSyncJob[]) => {
  const merged = new Map<string, OfflineReaderSyncJob>();
  for (const job of queue) {
    merged.set(job.key, job);
  }
  return Array.from(merged.values());
};

export const enqueueOfflineReaderSync = (job: OfflineReaderSyncJob) => {
  const queue = mergeQueue([...readQueue(), job]);
  writeQueue(queue);
};

export const flushOfflineReaderSyncQueue = async () => {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;

  const queue = readQueue();
  if (!queue.length) return;

  const remaining: OfflineReaderSyncJob[] = [];

  for (const job of queue) {
    try {
      if (job.type === "reading-progress") {
        if (!isUuid(job.payload.user_id) || !isUuid(job.payload.resource_id)) {
          continue;
        }
        const { error } = await supabase
          .from("reading_progress" as never)
          .upsert(job.payload as never, { onConflict: "user_id,resource_id" });
        if (error) remaining.push(job);
        continue;
      }

      if (job.type === "reading-session") {
        if (!isUuid(job.payload.id) || !isUuid(job.payload.user_id) || !isUuid(job.payload.resource_id)) {
          continue;
        }
        const { error } = await supabase
          .from("reading_sessions" as never)
          .upsert(job.payload as never, { onConflict: "id" });
        if (error) remaining.push(job);
        continue;
      }

      if (!isUuid(job.payload.id) || !isUuid(job.payload.user_id) || !isUuid(job.payload.resource_id)) {
        continue;
      }
      const { error } = await supabase
        .from("study_sessions" as never)
        .upsert(job.payload as never, { onConflict: "id" });
      if (error) remaining.push(job);
    } catch {
      remaining.push(job);
    }
  }

  writeQueue(mergeQueue(remaining));
};

export const getOfflineReaderSyncEventName = () => STORAGE_UPDATED_EVENT;
